<template>
  <div class="le-input-field" v-tooltip="inputTooltip">
    <le-bubble theme="dark" trigger="manual" :open="showError" :placement="errorPlacement" :hideOnTargetClick="true">
      <template slot="target">
        <input
          :class="['input', { 'has-error': hasError}, { 'disabled': !enabled }, { 'has-search': shouldShowSearchIcon }, { 'has-reset': shouldShowResetButton } ]"
          :style="inputPaddingStyle"
          ref="inputField"
          :placeholder="placeholder"
          :value="value"
          :disabled="!enabled"
          :maxlength="maxChars"
          @input="onInput"
          @focus="onFocus"
          @blur="onBlur"
          @keyup="onKeyUp"
          @keydown.enter.prevent="onSubmit"
          :id="id"
          :aria-describedby="ariaDescBy"
          :aria-labelledby="ariaLabelledBy"
        />
      </template>
      <template slot="content">
        {{ bubbleErrorMsg }}
      </template>
    </le-bubble>
    <i v-if="shouldShowSearchIcon" class="ico-12 ico-search search-icon"></i>
    <i v-if="shouldShowResetButton" class="ico-8 ico-input-field-reset reset-button" @click="onResetClick"></i>
  </div>
</template>

<script>
  import { includes, throttle, values } from 'lodash';
  import leBubble from '../le-bubble/le-bubble';
  import CONSTS from '../../../consts/consts';

  export default {
    name: 'le-input-field',
    components: {
      leBubble,
    },
    props: {
      id: {
        type: String,
      },
      placeholder: {
        type: String,
      },
      value: {
        type: String,
      },
      enabled: {
        type: Boolean,
        default: true,
      },
      errorPlacement: {
        type: String,
        default: 'right',
      },
      throttle: {
        type: Number,
      },
      maxChars: {
        type: Number,
        default: 99999,
      },
      minValue: {
        type: Number,
        default: null,
      },
      maxValue: {
        type: Number,
        default: null,
      },
      showResetInputButton: {
        type: Boolean,
      },
      inputPaddingLeft: {
        type: Number,
      },
      inputPaddingRight: {
        type: Number,
      },
      tooltipPlacement: {
        type: String,
        default: CONSTS.LE_BUBBLE.PLACEMENTS.RIGHT,
      },
      inputType: {
        type: String,
        validator: type => includes(values(CONSTS.LE_INPUT_FIELD.TYPE), type),
        default: CONSTS.LE_INPUT_FIELD.TYPE.TEXT,
      },
      ariaDescBy: {
        type: String,
      },
      ariaLabelledBy: {
        type: String,
      },
    },
    data() {
      return {
        showError: false,
        errorMsg: '',
        bubbleErrorMsg: '',
        tooltipText: '',
        previousValue: '',
      };
    },
    computed: {
      hasError() {
        return this.errorMsg.length > 0;
      },
      inputPaddingStyle() {
        const style = {};
        if (this.inputPaddingLeft) {
          style['padding-left'] = this.inputPaddingLeft;
        }
        if (this.inputPaddingRight) {
          style['padding-right'] = this.inputPaddingRight;
        }
        return style === {} ? '' : style;
      },
      shouldShowSearchIcon() {
        return this.inputType === CONSTS.LE_INPUT_FIELD.TYPE.SEARCH;
      },
      shouldShowResetButton() {
        return this.isValueNotEmpty && this.showResetInputButton;
      },
      isValueNotEmpty() {
        return this.value !== '' && this.value !== undefined;
      },
      inputTooltip() {
        return { content: this.tooltipText, placement: this.tooltipPlacement, html: false, autoHide: false };
      },
    },
    created() {
      if (this.throttle) {
        this.inputFunc = throttle(() => {
          this.emitInputChange();
        }, this.throttle, { leading: false, trailing: true });
      } else {
        this.inputFunc = this.emitInputChange;
      }
    },
    mounted() {
      this.updateTooltipText();
      // Check initial value passed in props.
      const isValid = this.validateByType(this.value, this.inputType);
      if (isValid) {
        this.previousValue = this.value;
      } else {
        this.clearInputValue();
      }
    },
    updated() {
      this.updateTooltipText();
    },
    methods: {
      updateTooltipText() {
        const hasEllipsis = this.$refs.inputField && this.$refs.inputField.scrollWidth > this.$refs.inputField.offsetWidth;
        this.tooltipText = hasEllipsis ? this.value : '';
      },
      onResetClick() {
        this.clearInputValue();
      },
      clearInputValue() {
        this.$refs.inputField.value = '';
        this.clearError();
        this.emitInputChange();
      },
      onInput() {
        const inputValue = this.$refs.inputField.value;
        const isValid = this.validateByType(inputValue, this.inputType);
        if (this.throttle) {
          this.$emit('typing', true);
        }
        if (isValid) {
          this.previousValue = inputValue;
          this.inputFunc();
        } else {
          // Restore previous value
          this.$refs.inputField.value = this.previousValue;
          this.$emit('type-validation-error');
        }
      },
      validateByType(inputValue, inputType) {
        let isValid;
        switch (inputType) {
          case CONSTS.LE_INPUT_FIELD.TYPE.POSITIVE_INTEGER_OR_ZERO:
            isValid = this.validatePositiveIntegerOrZero(inputValue);
            break;
          case CONSTS.LE_INPUT_FIELD.TYPE.NEGATIVE_OR_POSITIVE_FLOAT_OR_ZERO:
            isValid = this.validateFloatNegativePositiveOrZero(inputValue);
            break;
          case CONSTS.LE_INPUT_FIELD.TYPE.NEGATIVE_OR_POSITIVE_INTEGER_OR_ZERO:
            isValid = this.validateIntegerNegativePositiveOrZero(inputValue);
            break;
          default:
            isValid = true;
            break;
        }
        return isValid;
      },
      validatePositiveIntegerOrZero(inputValue) {
        const regex = /^\d+$/g;
        let isValid = false;
        if ((inputValue.length === 0 ||
                regex.test(inputValue)) &&
                this.isNumberRangeValid(inputValue)) {
          isValid = true;
        }
        return isValid;
      },
      validateFloatNegativePositiveOrZero(inputValue) {
        const regex = /^[-]?[\d]+\.?[\d]*$/g;
        let isValid = false;
        if ((inputValue.length === 0 ||
                (inputValue.length === 1 && inputValue === '-') ||
                regex.test(inputValue)) &&
                this.isNumberRangeValid(inputValue)) {
          isValid = true;
        }
        return isValid;
      },
      validateIntegerNegativePositiveOrZero(inputValue) {
        let isValid = false;
        // parseInt(inputValue, 10) == inputValue: compare integer with string. e.g. '-55' == -55
        if ((inputValue.length === 0 ||
                (inputValue.length === 1 && inputValue === '-') ||
                parseInt(inputValue, 10) == inputValue) && // eslint-disable-line
                this.isNumberRangeValid(inputValue)) {
          isValid = true;
        }
        return isValid;
      },
      isNumberRangeValid(inputValue) {
        let isValid = true;
        if (this.minValue !== null && this.minValue > inputValue) {
          isValid = false;
        }
        if (this.maxValue !== null && this.maxValue < inputValue) {
          isValid = false;
        }
        return isValid;
      },
      onKeyUp() {
        this.showError = false;
      },
      emitInputChange() {
        if (this.throttle) {
          this.$emit('typing', false);
        }
        this.$emit('input', this.$refs.inputField.value);
      },
      onFocus() {
        this.$emit('focus');
        this.showError = this.hasError;
        this.bubbleErrorMsg = this.errorMsg;
      },
      onBlur(event) {
        this.$emit('blur', event);
        this.showError = false;
      },
      onSubmit() {
        this.$emit('submit');
      },
      blur() {
        this.$refs.inputField.blur();
      },
      setError(showError, errorMsg) {
        if (showError) {
          this.errorMsg = errorMsg;
        } else {
          this.clearError();
        }
      },
      clearError() {
        this.showError = false;
        this.errorMsg = '';
      },
      setFocus() {
        this.$refs.inputField.focus(); // this works in chrome and FF
        setTimeout(() => {
          this.$refs.inputField.focus(); // this is workaround for Safari
          /* 0 and nextTick are not working, leave it 200 or make sure you test it before you change */
        }, 200);
      },
    },
    watch: {
      value: {
        handler() {
          if (this.$refs.inputField && this.$refs.inputField.value != this.value) { // eslint-disable-line
            const isValid = this.validateByType(this.value, this.inputType);
            if (isValid) {
              this.previousValue = this.value;
            }
          }
        },
        immediate: true,
      },
    },
  };
</script>

<style lang="scss" scoped>
  .le-input-field {
    position: relative;
    .reset-button {
      cursor: pointer;
      position: absolute;
      right: 10px;
      top: 0;
      bottom: 0;
      margin: auto;
    }
    .search-icon {
      position: absolute;
      left: 9px;
      top: 1px;
      bottom: 0;
      margin: auto;
    }
    @include themify {
      .input {
        @include ellipsis();
        box-sizing: border-box;
        outline: none;
        height: 32px;
        padding: 0 9px;
        width: 100%;
        line-height: 32px;
        font-size: 12px;
        font-family: Roboto;
        border: 1px solid getColor('inputBorder');
        background-color: getColor('inputBackground');
        color: getColor('inputText');
        &:hover {
          border-color: getColor('inputBorderHover');
        }
        &:focus {
          border-color: getColor('inputBorderFocus');
        }
        &.has-search {
          padding-left: 25px;
        }
        &.has-reset {
          padding-right: 25px;
        }
        &.has-error {
          border-color: getColor('error');
        }
        &::placeholder {
          font-style: italic;
          color: getColor('inputText');
        }
        &:-ms-input-placeholder { /* Internet Explorer 10-11 */
          font-style: italic;
          color: getColor('inputText');
        }
        &:-ms-input-placeholder { /* Microsoft Edge */
          font-style: italic;
          color: getColor('inputText');
        }
        &.disabled {
          border: 1px solid getColor('inputBorderDisabled');
          background-color: getColor('inputBackgroundDisabled');
          color: getColor('inputTextDisabled');
          &::placeholder {
            color: getColor('inputTextDisabledPlaceholder');
          }
          &:-ms-input-placeholder { /* Internet Explorer 10-11 */
            color: getColor('inputTextDisabledPlaceholder');
          }
          &:-ms-input-placeholder { /* Microsoft Edge */
            color: getColor('inputTextDisabledPlaceholder');
          }
        }
        &::-ms-clear { /* Internet Explorer 10-11 */
          display: none;
          width: 0;
          height: 0;
        }

      }
    }
    .input {

    }
  }
</style>
